import React, { memo } from 'react'
import isNumber from 'lodash/isNumber'
import isUndefined from 'lodash/isUndefined'
import LightningBoltIcon from '@signal/components/Icons/LightningBolt'
import HydrateSvgIcon from '@signal/components/Icons/utils/hydrateSvg'
import { getAppsDomain } from '@signal/utils/env/apiDomain'
import isRelativeUrl from '@signal/utils/isRelativeUrl'
import stripQueryParams from '@signal/utils/stripQueryParams'
import { stripDashesFromAnyGuids } from '@signal/utils/stripDashesFromGuid'
import { RENDER_AS_BOTH, RENDER_AS_HEADER, RENDER_AS_MENU } from './constants'

export const APPS_BASE_URL = 'https://apps.purecars.com'

export function findBestPrefixMatch(items, normalizedPathname) {
  return items.reduce((best, item) => {
    const normalizedLink = stripDashesFromAnyGuids(stripQueryParams(item.link))
    if (!normalizedPathname.startsWith(normalizedLink)) return best
    if (!best) return item
    const bestNormalizedLink = stripDashesFromAnyGuids(
      stripQueryParams(best.link)
    )
    return normalizedLink.length > bestNormalizedLink.length ? item : best
  }, undefined)
}

const PRIMARY_DOMAIN_SEGMENT_REGEX = /\/\/([^./]+)\./

function renderAsMenu(renderAs) {
  return (
    !renderAs || // no renderAs specified assumes menu
    renderAs === RENDER_AS_MENU ||
    renderAs === RENDER_AS_BOTH
  )
}

/**
 * this function hydrates an icon string, which should be a parseable <svg>, into an actual Signal SVG component
 * @param {Object} - properties that should be applied to the HydrateSvgIcon, including the svgString
 * @return {React.Component} - a Signal SVG component for this icon, or undefined if no icon string was provided
 */
function hydrateServiceMenuIcon({ svgString, ...rest }) {
  const MenuIcon = () => {
    return (
      <HydrateSvgIcon
        svgString={svgString}
        ErrorIcon={LightningBoltIcon}
        {...rest}
      />
    )
  }
  return svgString ? memo(MenuIcon) : undefined
}

/**
 * this function hydrates all the icons in a service menu returned from the service
 * @param {Array} rawMenu - the array of menu items returned from useMenuService
 * @param {Object} iconProps - properties that should be applied to the HydrateSvgIcon for colorization/size purposes
 * @param {String} baseUrl - the base URL to use
 * @return {Array} - the hydrated array of menu items returned from useMenuService
 */
export function hydrateServiceMenuIcons(
  rawMenu,
  iconProps,
  baseUrl = APPS_BASE_URL
) {
  let baseUrlQA
  // attempt to extract the "primary domain" (e.g. "primary" in https://primary.purecars.com/)
  const primaryDomainMatches = baseUrl.match(PRIMARY_DOMAIN_SEGMENT_REGEX)
  if (primaryDomainMatches.length > 1) {
    // use it to get a QA domain to compare against for
    const primaryDomain = primaryDomainMatches[1]
    baseUrlQA = baseUrl.replace(
      primaryDomainMatches[0],
      `//${primaryDomain}-qa.`
    )
  }
  return rawMenu.map(({ children, icon, link, ...rest }) => {
    if (link) {
      // also make any base URL relative
      if (link.startsWith(baseUrl)) {
        link = link.substr(baseUrl.length)
      } else if (baseUrlQA && link.startsWith(baseUrlQA)) {
        link = link.substr(baseUrlQA.length)
      }
    }
    return {
      ...rest,
      link,
      icon: hydrateServiceMenuIcon({ svgString: icon, ...iconProps }),
      children: children
        ? hydrateServiceMenuIcons(children, iconProps, baseUrl)
        : []
    }
  })
}

/**
 * this function filters the given service menu for items that should be rendered in the multi-app-menu (popover)
 * @param {Array} serviceMenu - the array of menu items returned from useMenuService or any subset thereof
 * @return {Array} - the array of menu items that should be rendered in the header
 */
export function filterServiceMenuItemsForPopover(serviceMenu) {
  return serviceMenu
    .filter(({ renderAs }) => renderAsMenu(renderAs))
    .map(({ children, ...rest }) => ({
      ...rest,
      subMenuItems: children ? filterServiceMenuItemsForPopover(children) : []
    }))
}

/**
 * this function returns the default menu service items and index to use in the event no match can be found to the
 * current user's URL in the menu service response
 * @param {Array} serviceMenu - the array of menu items returned from useMenuService
 * @return {Object} - an object that returns the default menu service item and index
 */
export function findDefaultMenuServiceIndexAndItems(serviceMenu = []) {
  const filteredMenu = filterServiceMenuItemsForPopover(serviceMenu)

  const findDefault = (items, rootIndex) =>
    items.reduce((found, { link, subMenuItems }, itemIndex) => {
      if (found) {
        return found
      }
      if (link && isRelativeUrl(link)) {
        if (!isUndefined(rootIndex)) {
          // we found a usable link in a submenu.  Use this submenu/root index
          return {
            items,
            rootIndex
          }
        } else if (!subMenuItems) {
          // we found a usable link in a root menu item without submenu children.  Use this root index with no submenu
          return {
            items: [],
            rootIndex: itemIndex
          }
        }
      }
      return subMenuItems
        ? findDefault(
            subMenuItems,
            isUndefined(rootIndex) ? itemIndex : rootIndex
          )
        : null
    }, null)

  // if unable to find a matching domain after searching, then use just the first thing in the menu.
  return (
    findDefault(filteredMenu) || {
      items: filteredMenu.length ? filteredMenu[0].subMenuItems : [],
      rootIndex: filteredMenu.length ? 0 : undefined
    }
  )
}

/**
 * this function attempts to find all the menu items with a given renderAs at the given pathname
 * @param {Array} serviceMenu - the array of menu items returned from useMenuService
 * @param {String} pathname - the pathname whose header nav destinations should be found
 * @param {String} renderAsMatch - one of the renderAs types to find (header, select)
 * @return {Array} - the array of menu items that should be rendered at the header at this pathname
 */
export function findMenuRenderAsAtPath(
  serviceMenu,
  pathname,
  renderAsMatch = RENDER_AS_HEADER
) {
  // this variable will be set inside the recursive algorithm below once the menu items we want to return are found
  let menuItemsFound = []

  const filterFoundItems = foundItems =>
    foundItems.filter(({ renderAs }) => renderAs === renderAsMatch)

  // use a recursive find to find the first destination that matches the given pathname
  // once we find that destination, we look backwards through the ancestors to find the nearest ancestor which has
  // matching menu items of the renderAs type we are looking for
  const findMatchingPath = (menuItems, ancestorMenuItems) =>
    menuItems.find(elem => {
      const { children, link } = elem

      if (pathname.endsWith(link)) {
        ancestorMenuItems = [menuItems, ...ancestorMenuItems]

        if (children) {
          // also check any children to see if there is a match there too.  if so, we want to use the children not the
          // parent (since it might be a rollup parent).  If a match is found in the child menuItemsFound will be set
          // to the children by the child call
          if (findMatchingPath(children, ancestorMenuItems)) {
            return true
          }
        }

        // look at all ancestors menu sets, nearest first, and bail when we find the first ancestor menu list which has
        // at least one menu item with a renderAs type that we are looking for
        // this allows us to support cases where the header entries are defined further up the hierarchy than selects
        ancestorMenuItems.find(menuItems => {
          const foundItems = filterFoundItems(menuItems)
          if (foundItems.length) {
            menuItemsFound = foundItems
            return true
          }
          return false
        })

        return true
      }

      return (
        children &&
        findMatchingPath(children, [menuItems, ...ancestorMenuItems])
      )
    })

  // kick off the recursion starting at the base of service menu, no ancestors yet
  findMatchingPath(serviceMenu, [])

  if (!menuItemsFound.length && renderAsMenu(renderAsMatch)) {
    return findDefaultMenuServiceIndexAndItems(serviceMenu).items
  }

  return menuItemsFound
}

/**
 * this function returns the index of the root item in the service menu related to the given path.  This is useful to
 * determine which menu item should be selected at the root of the menu in the new menu design
 * @param {Array} serviceMenu - the array of menu items returned from useMenuService
 * @param {String} pathname - the pathname where the app is currently navigated to
 * @param {Boolean} noExact - don't require an exact URL match
 * @return {Number} - the index of the element in the serviceMenu which contains the location currently navigated to
 */
export function findActiveServiceMenuRootItemIndex(
  serviceMenu,
  pathname,
  noExact = false
) {
  // this variable will be set inside the recursive algorithm below once the menu items we want to return are found
  let rootElemIndexFound

  // use a recursive find to find the first destination that matches the given pathname
  const findMatchingPath = (menuItems, rootElemIndex) =>
    menuItems.find((elem, elemIndex) => {
      const { children, link } = elem
      const linkUse = link === '' ? '/' : link

      if (
        linkUse &&
        (noExact
          ? pathname.startsWith(stripQueryParams(linkUse))
          : pathname === stripQueryParams(linkUse))
      ) {
        rootElemIndexFound = isNumber(rootElemIndex) ? rootElemIndex : elemIndex
        return elem
      }

      return (
        children &&
        findMatchingPath(
          children,
          isNumber(rootElemIndex) ? rootElemIndex : elemIndex
        )
      )
    })

  // kick off the recursion starting at the base of service menu, no ancestors yet
  findMatchingPath(serviceMenu)

  if (isUndefined(rootElemIndexFound)) {
    return noExact
      ? findDefaultMenuServiceIndexAndItems(serviceMenu).rootIndex
      : findActiveServiceMenuRootItemIndex(
          serviceMenu,
          pathname,
          true /*noExact*/
        )
  }

  return rootElemIndexFound
}

/**
 * this function returns a redirect URL obtained from the menuService.  It is defined as the first internal or PureCars
 * route returned from the menu service.
 * @param {Array} serviceMenu - the array of menu items returned from useMenuService
 * @param {Boolean} allowDifferentDomain - whether to allow redirection to a different domain than the local domain
 * @return {String} - the redirect URL to use, or null if no suitable redirect URL can be obtained
 */
export function getDefaultRedirect(
  serviceMenu = [],
  allowDifferentDomain = true
) {
  const filteredMenu = filterServiceMenuItemsForPopover(serviceMenu)

  const findRedirect = items =>
    items.reduce((found, { link, subMenuItems }) => {
      if (found) {
        return found
      }
      if (link && (allowDifferentDomain || isRelativeUrl(link))) {
        return link
      }
      return subMenuItems ? findRedirect(subMenuItems) : null
    }, null)

  return findRedirect(filteredMenu) || getAppsDomain()
}
